import { API_BASE_URL } from '@/utils/env';
import { getFreshToken } from '@/utils/auth';

/* Shared, cached fetch of the admin client list. Every admin screen (Dashboard,
   Accounting, Services, Raising, Command Center) used to fetch /auth/users/all
   independently — slow and repeated. This fetches once per session, dedupes
   concurrent callers, and hands back the cached array instantly afterwards.

   Perf: results are also persisted to localStorage with a short TTL and served
   stale-while-revalidate, so navigations AND full page reloads render instantly
   from cache while a fresh copy is fetched in the background. */

const LS_KEY = 'admin_users_cache_v1';
const TTL = 3 * 60 * 1000; // 3 minutes

let cache: any[] | null = null;
let inflight: Promise<any[]> | null = null;

function readLS(): any[] | null {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    // Never serve an EMPTY cached array — a transient earlier failure could have cached
    // `[]`, and serving it makes every admin screen look empty until a hard remount.
    if (raw && Array.isArray(raw.u) && raw.u.length > 0 && typeof raw.t === 'number' && Date.now() - raw.t < TTL) return raw.u;
  } catch { /* ignore */ }
  return null;
}
function writeLS(u: any[]) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ t: Date.now(), u })); } catch { /* quota/full — ignore */ }
}

async function fetchFresh(): Promise<any[]> {
  // Mint a fresh token from the live Firebase session so a stale/missing stored
  // token can't silently 401 the whole admin into an empty state.
  const token = await getFreshToken();
  try {
    const res = await fetch(`${API_BASE_URL}/auth/users/all`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) { console.error('users/all failed:', res.status); return cache || []; }
    const data = await res.json();
    cache = Array.isArray(data) ? data : (data.users || []);
    writeLS(cache!);
    return cache!;
  } catch (e) {
    console.error('users/all error:', e);
    return cache || [];
  }
}

export async function getAllUsers(force = false): Promise<any[]> {
  // Don't short-circuit on an empty in-memory cache — refetch so an earlier empty
  // result can never pin the whole admin to a blank state.
  if (!force && cache && cache.length) return cache;

  // Serve stale from localStorage instantly, then revalidate in the background.
  if (!force) {
    const ls = readLS();
    if (ls) {
      cache = ls;
      if (!inflight) { inflight = fetchFresh().finally(() => { inflight = null; }); }
      return ls;
    }
  }

  if (!force && inflight) return inflight;
  inflight = fetchFresh().finally(() => { inflight = null; });
  return inflight;
}

export function clearUsersCache() {
  cache = null;
  try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
}
